import { useCallback, useEffect, useState } from 'react';
import checkFeatureFlagByName from '../api/feature-flags/checkFeatureFlagByName';
import { analytics } from 'services/analytics';

/**
 * Flag que exige tener IBAN antes de configurar una aportación. Se resuelve por
 * `user_ids` en la tabla `feature_flags`, así que se puede abrir a una persona.
 */
export const REQUIRE_IBAN_FEATURE_FLAG = 'require-iban-for-contribution';

export const IBAN_GATE_EVENTS = {
	/** Se ha parado a alguien: es el tamaño real del problema en producción. */
	BLOCKED: 'iban_gate_blocked',
	/**
	 * Importe que el usuario había elegido antes del desvío. Se guarda para que
	 * al volver del mini-KYC no tenga que decidirlo otra vez.
	 */
	PENDING_AMOUNT_KEY: 'iban_gate_pending_amount'
} as const;

/** Desde qué CTA se paró al usuario. */
export type IbanGateSource = 'one_time' | 'periodic';

export interface ShouldGateContributionParams {
	flagEnabled: boolean;
	hasPersonalPlan: boolean;
	bankAccount: string | null | undefined;
	/**
	 * Si ya sabemos qué cuenta tiene esta persona. `bankAccount` vacío no basta
	 * para decidir: significa a la vez «todavía no lo sé» y «no tiene».
	 */
	bankAccountResolved: boolean;
}

/**
 * ¿Hay que parar a esta persona antes de dejarle configurar una aportación?
 *
 * El gate se cuelga del **plan**, no del tipo de usuario: lo que necesita IBAN
 * no es una clase de persona, es un plan personal domiciliado — uno de empleo
 * no lo necesita aunque sea la misma persona. Por eso no se lee
 * `users.is_individual`, que además está en revisión y va a desaparecer.
 *
 * Mientras el plan no está resuelto tampoco se bloquea: enseñar el aviso en un
 * parpadeo de carga sería peor que no enseñarlo.
 */
export const shouldGateContribution = ({
	flagEnabled,
	hasPersonalPlan,
	bankAccount,
	bankAccountResolved
}: ShouldGateContributionParams): boolean =>
	flagEnabled && hasPersonalPlan && bankAccountResolved && !(bankAccount ?? '').trim();

export interface UseIbanGateParams {
	token: string | null | undefined;
	companyId: string | null | undefined;
	userId: string | null | undefined;
	/** `users.bank_account`, tal cual llega de `current-user`. */
	bankAccount: string | null | undefined;
	/**
	 * Si `current-user` ya respondió. Sin esto, un fallo de red dejaría
	 * `bankAccount` en su valor inicial y bloquearíamos a quien SÍ tiene cuenta,
	 * que es justo lo contrario del fail-safe.
	 */
	bankAccountResolved: boolean;
	/** Si esta vista ya resolvió el plan personal del usuario. */
	hasPersonalPlan: boolean;
}

export interface UseIbanGateResult {
	blocked: boolean;
	loading: boolean;
	/**
	 * Deja constancia del desvío: evento de embudo y, si venía de la periódica,
	 * el importe elegido para retomarlo al volver.
	 */
	notifyBlocked: (source: IbanGateSource, amount?: number) => void;
}

/**
 * Gate de IBAN sobre los dos CTA de aportación de la vista individual.
 *
 * Sin cuenta bancaria no se puede cobrar: hoy la aportación puntual revienta
 * con un 500 (el body de Caser convierte un IBAN vacío y lanza) y la periódica
 * se deja configurar tan tranquila para quedarse esperando un cargo que no
 * puede salir. Este hook decide si hay que desviar a la persona al mini-KYC de
 * banco y firma antes de dejarla llegar a la pantalla de importe.
 *
 * **Ante cualquier duda, sin bloquear**: sin token, sin usuario o con la
 * comprobación de la flag fallando, la vista se comporta como siempre. Es el
 * mismo criterio que `useOneTimeContributionEnabled`, y aquí importa igual: un
 * fallo de red no puede dejar a alguien sin poder aportar.
 *
 * No hace ninguna llamada nueva para el IBAN: `bankAccount` ya viene en el
 * `current-user` que la vista pide de todos modos.
 */
const useIbanGate = ({
	token,
	companyId,
	userId,
	bankAccount,
	hasPersonalPlan,
	bankAccountResolved
}: UseIbanGateParams): UseIbanGateResult => {
	const [flagEnabled, setFlagEnabled] = useState(false);
	// Nace diciendo la verdad: si hay a quién preguntar, se va a preguntar. Con
	// `false` inicial el hook afirmaba «ya lo sé» en la única ventana en la que
	// no lo sabe, y quien lo consume no podía distinguirla.
	const [loading, setLoading] = useState(() => Boolean(token && userId));

	const check = useCallback(async () => {
		if (!token || !userId) {
			setFlagEnabled(false);
			return;
		}

		setLoading(true);

		try {
			const res = await checkFeatureFlagByName(
				token,
				companyId ?? '',
				userId,
				REQUIRE_IBAN_FEATURE_FLAG
			);
			setFlagEnabled(!!(res?.success && res.data?.isActive === true));
		} catch {
			setFlagEnabled(false);
		} finally {
			setLoading(false);
		}
	}, [token, companyId, userId]);

	useEffect(() => {
		check();
	}, [check]);

	const notifyBlocked = useCallback((source: IbanGateSource, amount?: number) => {
		if (amount !== undefined) {
			sessionStorage.setItem(IBAN_GATE_EVENTS.PENDING_AMOUNT_KEY, String(amount));
		}

		analytics.track(IBAN_GATE_EVENTS.BLOCKED, {
			source,
			...(amount !== undefined ? { amount } : {})
		});
	}, []);

	return {
		blocked: shouldGateContribution({
			flagEnabled,
			hasPersonalPlan,
			bankAccount,
			bankAccountResolved
		}),
		loading,
		notifyBlocked
	};
};

export default useIbanGate;
